import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Drawer, Grid, Select, Table, Tag } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import {
  useApiKeysList,
  useUpdateApiKeyStatus,
} from '../../../../../../../Hooks/TT_ELD/Companies';
import useRouteQuery from '../../../../../../../Utils/useRouterQuery';
import SearchInput from '../../../../../../../Utils/SearchInput';

const { useBreakpoint } = Grid;
const { Option } = Select;

type params = {
  readonly id: any;
};

const ApiKeys = () => {
  const { id } = useParams<params>();

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [skip, setSkip] = useRouteQuery('1', 'skip', String);
  const [isActive, setIsActive] = useRouteQuery(true, 'isActive', Boolean);
  const [search, setSearch] = useRouteQuery('', 'search', (v) => String(v));
  const [updatingApiKeyId, setUpdatingApiKeyId] = useState<string | number | null>(null);

  const { mutate: updateApiKeyStatus } = useUpdateApiKeyStatus();

  const { data, isLoading, refetch } = useApiKeysList({
    companyId: id,
    isActive,
    page: skip,
    perPage: 10,
    search,
  });

  const onChange = (query: any) => setSkip(query.current);

  const handleChange = (event: string) => {
    setSearch(event.trim());
    setSkip('1');
  };

  const handleToggleApiKeyStatus = useCallback(
    (record: any) => {
      if (!record?.id || !id) return;

      setUpdatingApiKeyId(record.id);

      updateApiKeyStatus(
        {
          id: record.id,
          companyId: id,
          is_active: !Boolean(record?.is_active),
        },
        {
          onSuccess: () => {
            refetch();
          },
          onSettled: () => {
            setUpdatingApiKeyId(null);
          },
        },
      );
    },
    [id, refetch, updateApiKeyStatus],
  );

  const columns = useMemo(
    () => [
      {
        title: 'No',
        dataIndex: 'no',
        key: 'no',
        width: 70,
        render: (_: any, __: any, index: number) => index + 1 + (Number(skip) - 1) * 10,
      },
      { title: 'Name', dataIndex: 'name', key: 'name' },
      { title: 'API Key', dataIndex: 'apiKey', key: 'apiKey' },
      {
        title: 'Status',
        dataIndex: 'is_active',
        key: 'status',
        render: (v: boolean) =>
          v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
      },
      { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt' },
      { title: 'Updated At', dataIndex: 'updatedAt', key: 'updatedAt' },
      {
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        width: 170,
        render: (_: any, record: any) => {
          const isCurrentStatusActive = Boolean(record?.is_active);
          return (
            <Button
              type={isCurrentStatusActive ? 'default' : 'primary'}
              danger={isCurrentStatusActive}
              loading={updatingApiKeyId === record?.id}
              onClick={() => handleToggleApiKeyStatus(record)}
            >
              {isCurrentStatusActive ? 'Deactivate' : 'Activate'}
            </Button>
          );
        },
      },
    ],
    [skip, updatingApiKeyId, handleToggleApiKeyStatus],
  );

  const filtersUI = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <SearchInput
          placeholder="Search"
          value={search}
          onDebounceChange={handleChange}
          searchWidth="100%"
        />

        <Select
          style={{ width: '100%' }}
          value={isActive}
          onChange={(value: boolean) => {
            setIsActive(value);
            setSkip('1');
          }}
        >
          <Option value={true}>Active</Option>
          <Option value={false}>Inactive</Option>
        </Select>

        {isMobile && (
          <div style={{ display: 'flex', gap: 10, width: '100%' }}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                refetch();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </div>
        )}
      </div>
    ),
    [isMobile, search, isActive],
  );

  return (
    <div className="mainPage">
      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <SearchInput placeholder="Search" value={search} onDebounceChange={handleChange} />

            <Select
              style={{ width: 200 }}
              value={isActive}
              onChange={(value: boolean) => {
                setIsActive(value);
                setSkip('1');
              }}
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </div>

          <Button size="large" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            icon={<FilterOutlined />}
            onClick={() => setFiltersOpen(true)}
          />
          <Button block size="large" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      )}

      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      <Table
        loading={isLoading}
        dataSource={data?.data}
        columns={columns as any}
        onChange={onChange}
        rowKey={(r: any) => r?.id ?? r?.apiKey ?? `${r?.name}-${r?.createdAt}`}
        pagination={{ total: data?.count, showSizeChanger: false, current: Number(skip) }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default ApiKeys;
